/**
 * Role and permission model.
 *
 * Expressed as data rather than scattered conditionals so the rules can be read
 * in one place and tested exhaustively. See CLAUDE.md — a missed check here is a
 * privilege escalation, not a cosmetic bug.
 *
 * The ladder, in the words the UI uses: member (user), creator, admin, owner
 * (super_admin). Members read. Creators make things but control nobody.
 * Admins run the workspace's people — creators and members, never other
 * admins. Owners manage admins and can mint other owners.
 */

export const ROLES = ["user", "creator", "admin", "super_admin"] as const;
export type Role = (typeof ROLES)[number];

/**
 * Turn whatever the database or a request carried into a role.
 *
 * "manager" is the pre-rename spelling of "creator" and is honoured forever:
 * rows written by the previous release must not silently demote to `user`,
 * which is exactly what an unknown string does.
 */
export function normalizeRole(value: string | null | undefined): Role {
  if (value === "manager") return "creator";
  return (ROLES as readonly string[]).includes(value ?? "") ? (value as Role) : "user";
}

/** Higher number outranks lower. Used only for the "can act on" comparison. */
const RANK: Record<Role, number> = {
  user: 0,
  creator: 1,
  admin: 2,
  super_admin: 3,
};

export const PERMISSIONS = [
  // content
  "recording:view",
  "recording:edit",
  "recording:delete",
  "project:view",
  "project:manage",
  "chat:use",
  // configuration
  "bot:configure",
  "tenant:settings",
  "credentials:manage",
  // people
  "member:invite",
  "member:remove",
  "member:block",
  "member:change_role",
  // dangerous
  "admin:manage",
  "super_admin:grant",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/** Members are readers: watch, search, chat. */
const BASE: Permission[] = ["recording:view", "project:view", "chat:use"];

/** Creators make and edit content; people and settings stay out of reach. */
const CREATOR: Permission[] = [
  ...BASE,
  "recording:edit",
  "recording:delete",
  "project:manage",
  "bot:configure",
];

const ADMIN: Permission[] = [
  ...CREATOR,
  "tenant:settings",
  "credentials:manage",
  "member:invite",
  "member:remove",
  "member:block",
  "member:change_role",
];

const SUPER_ADMIN: Permission[] = [...ADMIN, "admin:manage", "super_admin:grant"];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  user: BASE,
  creator: CREATOR,
  admin: ADMIN,
  super_admin: SUPER_ADMIN,
};

export function can(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/**
 * Whether `actor` may modify `target`'s membership — change their role, remove,
 * or block them.
 *
 * The rule the product asks for: an admin manages everyone below admin but
 * cannot touch another admin. An owner manages everyone — including other
 * owners, so a compromised or departed owner is recoverable from inside the
 * workspace rather than being a support ticket.
 *
 * Acting on yourself is refused at the routes (`cannot_modify_self`), not
 * here: this function compares roles and two owners are the same role.
 * Owner-on-owner action is additionally behind an explicit danger
 * confirmation and a last-owner guard at the route.
 */
export function canActOn(actor: Role, target: Role): boolean {
  if (!can(actor, "member:change_role")) return false;
  if (actor === "super_admin") return true;
  return RANK[target] < RANK[actor];
}

/** Roles `actor` is allowed to assign to someone else. */
export function assignableRoles(actor: Role): Role[] {
  if (actor === "super_admin") return ["user", "creator", "admin", "super_admin"];
  if (actor === "admin") return ["user", "creator"];
  return [];
}

/**
 * Touching owner status needs an explicit, scary confirmation in the UI —
 * granting it, taking it away, or removing an owner from the workspace.
 */
export function requiresDangerConfirmation(role: Role): boolean {
  return role === "super_admin";
}
